import { Box, Fade, Slide, useTheme } from '@mui/material';
import React, { MouseEventHandler, SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { Theme } from '../../../../themes/themes';
import { FinalContainer } from '../../../FinalGrid/FinalGrid';
import { Flyout } from '../../../Flyout/Flyout';
import { HeaderBar, HeaderBarProps } from '../../../HeaderBar/HeaderBar';
import type { FlatTree } from '../../flatTree';
import { calcMaxLevelFromTree, isLast, itemsByLevel, itemsByParent } from '../../flatTree';
import { useNavigation } from '../../Provider';
import { TabPanel } from '../../TabPanel';
import { useStyles } from './Desktop.styles';
import { navigate } from './navigate';

interface TabProps {
  tab: number;
  index: number;
  levels: Array<string>;
  item: FlatTree;
  panels: Array<FlatTree>;
  tree: Array<FlatTree>;
  nextPanel: (event: SyntheticEvent, item: FlatTree) => void;
}

const Tab: React.FC<TabProps> = ({ tab, index, levels, item, panels, tree, nextPanel }) => {
  const styles = useStyles();
  const { isInteractive } = useNavigation();
  const isActive = (item: FlatTree) => panels.indexOf(item) !== -1;

  const isTabActive = tab === index;
  if (!isTabActive) {
    return null;
  }

  return (
    <TabPanel key={`panel-${item.id}`} value={tab} index={index} sx={styles.panels}>
      {levels.map((value: string, levelIndex: number) => {
        const level = itemsByLevel(tree, levelIndex);
        const levelByRoot =
          levelIndex === 0
            ? level.filter((levelItem) => levelItem.id === item.id)
            : level.filter((levelItem) => levelItem.root === item.id);

        const activeLevel = panels.length - 1;
        const activePanelId = levelIndex <= activeLevel ? panels[levelIndex].id : 0;

        let anyLevelGroupVisible = false;
        const levelGroups = levelByRoot.reduce((pv, item) => {
          const items = itemsByParent(tree, item.id);
          if (items.length) {
            anyLevelGroupVisible = anyLevelGroupVisible || activePanelId === item.id;
            pv.push({
              items,
              renderInitial: items[0].renderInitial,
              visible: activePanelId === item.id,
              id: item.id,
            });
          }
          return pv;
        }, []);

        return (
          <Box
            key={`${value}-${item.id}`}
            sx={styles.panelX}
            style={{
              zIndex: 100 - levelIndex,
              borderLeftStyle: anyLevelGroupVisible ? 'solid' : 'none',
            }}
            data-selector={`navigation.${value}-${item.id}`}
          >
            {levelGroups.map((levelGroup) => {
              if (!levelGroup.visible && isInteractive) {
                return null;
              }
              if (!levelGroup.renderInitial && !isInteractive) {
                return null;
              }

              return (
                <Box
                  key={levelGroup.id}
                  sx={styles.panelZ}
                  style={{
                    zIndex: 100 - levelIndex,
                  }}
                  data-selector={`navigation.z-${value}-${item.id}-${levelGroup.id}`}
                >
                  {levelGroup.items
                    .sort((a: FlatTree, b: FlatTree) => a.order - b.order)
                    .map((item: FlatTree, index: number) => {
                      const linkComponent = item.link({
                        nextPanel: (e: SyntheticEvent) => nextPanel(e, item),
                        isLast: isLast(tree, item.id),
                        isActive: isActive(item),
                        variant: 'desktop',
                      });

                      return (
                        <Slide
                          in={levelGroup.visible}
                          key={`slide-${item.id}`}
                          direction="right"
                          timeout={{ enter: 50, exit: 360 }}
                          style={{
                            transitionDelay: `${index * 43}ms`,
                          }}
                        >
                          {linkComponent}
                        </Slide>
                      );
                    })}
                </Box>
              );
            })}
          </Box>
        );
      })}
    </TabPanel>
  );
};

export interface DesktopProps {
  tree: FlatTree[];
  open: boolean;
  HeaderBarProps: HeaderBarProps;
  DesktopProps: {
    tabSize: 'small' | 'medium' | 'large' | 'header';
  };
  onClose: MouseEventHandler<HTMLOrSVGElement>;
}

export const Desktop: React.FC<DesktopProps> = ({ tree, open, HeaderBarProps, onClose }) => {
  const theme = useTheme<Theme>();
  const styles = useStyles();
  const { activeTab, setActiveTab } = useNavigation();
  const [showHeaderBar, setShowHeaderBar] = useState<boolean>(false);
  const tabs = useMemo(() => itemsByLevel(tree, 0).sort((a: FlatTree, b: FlatTree) => a.order - b.order), [tree]);
  const [tab, setTab] = useState<number>(0);
  const [panels, setPanels] = useState<Array<FlatTree>>([]);
  const headerRef = useRef<HTMLDivElement>(null);
  const [margin, setMargin] = useState(207.5); // TODO: Reflow in Navigation is very slow, we should refactor to not need to this manual marign calculation
  const boxStyles = Object.assign({}, styles.header, styles.isDesktop);

  const changeTab = useCallback(
    (value: number) => {
      setTab(value);
      setPanels([tabs[value]]);
    },
    [tabs]
  );

  const onExited = useCallback(() => setShowHeaderBar(false), []);
  const onEnter = useCallback(() => setShowHeaderBar(true), []);

  const nextPanel = useCallback(
    (event: SyntheticEvent, item: FlatTree) => {
      event.preventDefault();
      if (item.parent) {
        setPanels((panels) => navigate(panels, tree, item));
      }
    },
    [tree]
  );

  const reset = () => {
    setPanels([]);
    setActiveTab(0);
  };

  useEffect(() => {
    const resize = () => {
      if (headerRef?.current) {
        const left = headerRef?.current?.getBoundingClientRect().left;
        setMargin(left);
      } else {
        console.error('Missing headerRef?');
      }
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, [showHeaderBar]);

  const levels = useMemo(() => [...new Array(calcMaxLevelFromTree(tree))].map((_, index) => `level-${index}`), [tree]);
  return (
    <>
      <Box sx={styles.offContainer}>
        <FinalContainer>
          <Box ref={headerRef} />
        </FinalContainer>
      </Box>
      <Box sx={boxStyles}>
        <Fade in={open} timeout={{ enter: 360, exit: 360 }} onExited={onExited} onEnter={onEnter}>
          <Box>
            {showHeaderBar && (
              <HeaderBar
                variant="flyout"
                id={HeaderBarProps.id}
                mobileIcon={React.cloneElement(HeaderBarProps.mobileIcon, { sx: { fill: theme.palette.primary.main } })}
                level1Navigation={<Box sx={styles.hidden}>{HeaderBarProps.level1Navigation}</Box>}
                hideLevel1navigation
                level2Navigation={
                  <Box sx={styles.level2Navigation} role="tablist">
                    {tabs.map((item: FlatTree, index: number) => {
                      return item.button({
                        nextTab: () => changeTab(index),
                        isActive: tab === index,
                        isLast: isLast(tree, item.id),
                      });
                    })}
                  </Box>
                }
                searchButton={<Box sx={styles.hidden}>{HeaderBarProps.searchButton}</Box>}
                button={<Box sx={styles.hidden}>{HeaderBarProps.button}</Box>}
                customFlyoutLogoUrl={HeaderBarProps.customFlyoutLogoUrl}
                customLogoUrl={HeaderBarProps.customLogoUrl}
              />
            )}
          </Box>
        </Fade>
      </Box>
      <Flyout
        open={open}
        anchor="left"
        sx={styles.isDesktop}
        transitionDuration={{ enter: 500, exit: 500 }}
        PaperProps={{ sx: styles.paper }}
        SlideProps={{
          onExited: () => reset(),
          onEntered: () => {
            const foundTab = tabs.find((tab) => tab.related === activeTab);
            const foundTabIndex = tabs.findIndex((tab) => tab.related === activeTab);

            changeTab(foundTabIndex !== -1 ? foundTabIndex : 0);

            if (foundTab) {
              document.getElementById(`navigation-tab-${foundTab.related}`)?.focus();
            }
          },
        }}
        ModalProps={{
          disablePortal: true,
          keepMounted: true,
          disableAutoFocus: true,
          disableEnforceFocus: true,
        }}
        onClose={onClose}
        role="navigation"
      >
        <Box sx={{ ...styles.content, marginLeft: `${margin}px` }}>
          {tabs.map((item: FlatTree, index: number) => (
            <Tab
              key={index}
              tab={tab}
              index={index}
              levels={levels}
              item={item}
              panels={panels}
              nextPanel={nextPanel}
              tree={tree}
            />
          ))}
        </Box>
      </Flyout>
    </>
  );
};
